/**
 * Line-level ship-to backfill, keyed on RESOLVABILITY (Ashok ruling,
 * 2026-08-05 - supersedes the emptiness discriminator shipped in 6ca48b3).
 *
 * WHY EMPTINESS FAILED: 6ca48b3 backfilled only lines whose
 * ship_to_location was blank. The refinement agent fills EVERY line from
 * the free-text slot at refinement_agent.txt:123, so no line is ever
 * blank and the backfill never fired. Confirmed inert on staging
 * PROC-000308: grid blank, lines carrying "London Office, Site Code:
 * GUR002", all 9 docx rows printing it.
 *
 * WHY NOT PROVENANCE: source/modified cannot carry this. modified is
 * reconstructed server-side at finalize-line-items
 * (routes/procurement.py:2815-2821) by guessing a line_number match - it
 * is not a record of user action, and there is a reachable path (resume
 * at status "refined", where loadProcurement hydrates from
 * requirement_line_items, which carry no provenance at all) on which a
 * genuine user edit is stored as source "ai", modified false. Building
 * the overwrite rule on that would silently destroy buyer edits. Ruled
 * out; the provenance repair is a separate item.
 *
 * THE RULE: a line's ship_to_location is overwritten with the buyer's
 * resolved answer if and only if its current value does not resolve
 * against location master. A valid code is preserved regardless of
 * provenance - LineItemsEditor's location control is a Select whose
 * options come only from the master list (LineItemsEditor.jsx:725-732),
 * so a non-resolving value cannot have originated from the buyer. It can
 * only have come from the agent's free-text slot, which is exactly what
 * this replaces.
 *
 * FAIL-SAFE: no locations list means the membership test is untestable,
 * and an untestable test must never authorize a write. Empty or missing
 * list => every line is returned untouched. This is the difference
 * between "we know this value is wrong" and "we could not check" - only
 * the first justifies overwriting buyer-visible data.
 *
 * PHASE 1 SCOPE: this decides WHICH lines take the resolved value. It
 * does not validate what the agent emitted anywhere else, and it is not
 * backend enforcement - that remains Phase 2.
 */

/** Master codes as a Set. GET /locations returns {location_id, name, address}. */
const toValidCodeSet = (locations) => new Set(
  (Array.isArray(locations) ? locations : [])
    .map((loc) => (typeof loc === 'string' ? loc : loc?.location_id))
    .filter(Boolean)
    .map((code) => String(code).trim())
);

/**
 * @param lineItems      current lines
 * @param resolvedLocation the buyer's resolved ship-to (answer first - see
 *   WizardContext's backfillLocation)
 * @param locations      master list from GET /locations; empty/missing
 *                       triggers the fail-safe
 * @returns a new array only where something actually changed; unchanged
 *   lines keep their original object identity, so "no write" is
 *   observable by reference and a no-op cannot masquerade as a write.
 */
export const applyResolvedLocationToLines = (lineItems, resolvedLocation, locations) => {
  const items = Array.isArray(lineItems) ? lineItems : [];
  const validCodes = toValidCodeSet(locations);

  // FAIL-SAFE: list absent, still loading, or empty - membership is
  // untestable, so nothing is authorized.
  //
  // HONESTY NOTE (mutation-testing result, 2026-08-05): this line is
  // REDUNDANT BY CONSTRUCTION today - deleting it alone fails no test,
  // because the replacement check below independently returns `items`
  // when the set is empty (nothing can resolve against an empty set).
  // Kept deliberately, not by oversight: it states the safety property at
  // the point it applies, and it is what still holds the line if the
  // check below is ever relaxed (e.g. "write the answer even if master
  // doesn't list it"). Deleting BOTH does fail six tests, so the BEHAVIOUR
  // is pinned even though this specific line is not individually load-
  // bearing. Do not treat a passing suite as licence to drop it.
  if (validCodes.size === 0) return items;

  // Never write a value we cannot ourselves vouch for. With no answer the
  // caller passes the agent's emission as a fallback; if that is prose it
  // fails this check and every line is left alone, which is why "no
  // answer" leaves lines unchanged in practice.
  const replacement = (resolvedLocation || '').toString().trim();
  if (!replacement || !validCodes.has(replacement)) return items;

  return items.map((item) => {
    const current = (item?.ship_to_location || '').toString().trim();
    // Resolves against master - preserved regardless of provenance. Covers
    // "already carries the answer" too: same object back, no write.
    if (validCodes.has(current)) return item;
    return { ...item, ship_to_location: replacement };
  });
};
